import axios from 'axios';
import { ZodTypeAny } from 'zod';
import { analyticsDefaultFilterBody, analyticsTabDataBody, emissionsFilters } from '@carboncare/schemas';
import { preflight } from './api/schemaPreflight';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

// Create axios instance for analytics with authentication
const analyticsApi = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
analyticsApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for handling errors
analyticsApi.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('Analytics API Error:', error);
    if (error.response?.status === 401) {
      // Clear token and redirect to login if unauthorized
      localStorage.removeItem('authToken');
      globalThis.location.href = '/auth/login';
    }
    return Promise.reject(error);
  }
);

export const analyticsEndpoints = {
  // Analytics Header - KPI cards
  analyticsHeader: '/analytics/analytics-header',

  // Default Filter
  analyticsDefaultFilter: '/analytics/analytics-default-filter',

  // Tab Info - Available tabs
  tabInfo: '/analytics/analytics-tab-info',

  // Tab Data - Dimensional analysis
  tabData: '/analytics/analytics-tab-data',

  // Generate Shipments Excel Export
  generateShipmentsExcelExport: '/analytics/generate-shipments-excel-export',

  // Get Shipments Exports
  getShipmentsExports: '/analytics/shipments-excel-exports',

  // Filter endpoints
  filters: {
    // NEW: Optimized hierarchical filter endpoint (single call, pre-aggregated)
    filterHierarchy: '/filter/filter-hierarchy',

    // Per-dimension lazy search (ILIKE beyond the hierarchy's 1000-item cap)
    search: '/filter/search',

    // Legacy individual filter endpoints (kept for backward compatibility)
    licensee: '/filter/panel/licensee',
    subCompany: '/filter/panel/subcompany',
    branch: '/filter/panel/branch',
    subCustomer: '/filter/panel/subcustomer',
    countryOrigin: '/filter/panel/country/origin',
    countryDestination: '/filter/panel/country/destination',
    cityOrigin: '/filter/panel/city/origin',
    cityDestination: '/filter/panel/city/destination',
    transportMode: '/filter/panel/transport',
  },
};

// Helper function to make GET requests
export const getData = async (url: string, params?: any) => {
  const response = await analyticsApi({
    method: 'GET',
    url,
    params,
  });
  return response;
};

// Same contracts the backend enforces — preflight before the network call
const BODY_SCHEMAS: Record<string, ZodTypeAny> = {
  [analyticsEndpoints.analyticsHeader]: emissionsFilters,
  [analyticsEndpoints.analyticsDefaultFilter]: analyticsDefaultFilterBody,
  [analyticsEndpoints.tabData]: analyticsTabDataBody,
  [analyticsEndpoints.generateShipmentsExcelExport]: emissionsFilters,
};

// Helper function to make POST requests
export const postData = async (url: string, body?: any) => {
  const schema = BODY_SCHEMAS[url];
  if (schema) preflight(schema, body ?? {});
  const response = await analyticsApi({
    method: 'POST',
    url,
    data: body,
  });
  return response;
};

// Helper function to make PATCH requests (for filters)
export const patchData = async (url: string, body?: any) => {
  if (url.includes('/panel/')) preflight(emissionsFilters, body ?? {});
  const response = await analyticsApi({
    method: 'PATCH',
    url,
    data: body,
  });
  return response;
};

const analyticsService = {
  getData,
  postData,
  patchData,
  endpoints: analyticsEndpoints,
};

export default analyticsService;
